import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Save, FileSearch, AlertTriangle, Send, X, CheckCircle, Plus, Trash2, Download } from 'lucide-react';
import { primaFacieService, PrimaFacieAssignmentDetail, CreateUpdatePrimaFacieReviewPayload } from '../services/adminAuth';
import { useNotification } from '../../sharedComponents/Notifications';
import { submissionVersionService } from '../services/adminAuth';
import { downloadFileFromUrl } from '../../../utils/downloadHelper';
import { FILE_BASE_URL } from '../../../api/axios';

const PRIMA_FACIE_PRINT_STYLES = `
@media print {
    @page { size: A4; margin: 14mm 12mm; }
    body, html { margin: 0 !important; padding: 0 !important; background: white !important; }
    aside, nav { display: none !important; }
    .fixed { display: none !important; }
    .w-full.max-w-6xl { max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
    .pf-print-doc, .pf-print-doc * {
        color: #000 !important;
        background: transparent !important;
        box-shadow: none !important;
    }
    .pf-print-doc { background: white !important; }
    .pf-print-section { page-break-inside: avoid; break-inside: avoid; }
    h1, h2, h3, h4 { break-after: avoid; page-break-after: avoid; }
}
`;

const ROField = ({ label, value, fullWidth = false }: { label: string; value: string; fullWidth?: boolean }) => (
    <div className={fullWidth ? 'col-span-full' : ''}>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">{label}</p>
        <div className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 min-h-[38px]">
            {value ? value : <span className="italic text-gray-400">—</span>}
        </div>
    </div>
);

const ROTextArea = ({ label, value, rows = 3, fullWidth = false }: {
    label: string; value: string; rows?: number; fullWidth?: boolean;
}) => (
    <div className={fullWidth ? 'col-span-full' : ''}>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">{label}</p>
        <div
            className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-800"
            style={{ minHeight: `${rows * 1.75}rem` }}
        >
            {value ? value : <span className="italic text-gray-400">—</span>}
        </div>
    </div>
);

const ROYesNo = ({ label, value }: { label: string; value: string }) => (
    <div>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-2">{label}</p>
        <div className="flex gap-6">
            {['YES', 'NO'].map(opt => (
                <label key={opt} className="flex items-center gap-2 opacity-70 cursor-default">
                    <input type="radio" checked={value === opt} readOnly className="accent-[#088A61] w-4 h-4" />
                    <span className="text-sm font-semibold text-gray-700">{opt}</span>
                </label>
            ))}
        </div>
    </div>
);

const Field = ({ label, value, onChange, placeholder = '', fullWidth = false, type = 'text' }: {
    label: string; value: string; onChange: (v: string) => void;
    placeholder?: string; fullWidth?: boolean; type?: string;
}) => (
    <div className={fullWidth ? 'col-span-full' : ''}>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">{label}</p>
        <input
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent transition-all"
        />
    </div>
);

const TextArea = ({ label, value, onChange, rows = 3, fullWidth = false, placeholder = '' }: {
    label: string; value: string; onChange: (v: string) => void;
    rows?: number; fullWidth?: boolean; placeholder?: string;
}) => (
    <div className={fullWidth ? 'col-span-full' : ''}>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">{label}</p>
        <textarea
            rows={rows}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent resize-none transition-all"
        />
    </div>
);

/** YesNo where the value is "YES" | "NO" (string) */
const YesNo = ({ label, name, value, onChange }: {
    label: string; name: string; value: string; onChange: (v: string) => void;
}) => (
    <div>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-2">{label}</p>
        <div className="flex gap-6">
            {['YES', 'NO'].map(opt => (
                <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name={name} checked={value === opt} onChange={() => onChange(opt)}
                        className="accent-[#088A61] w-4 h-4" />
                    <span className="text-sm font-semibold text-gray-700">{opt}</span>
                </label>
            ))}
        </div>
    </div>
);

const SectionCard = ({ title, accent = false, children }: {
    title?: string; accent?: boolean; children: React.ReactNode;
}) => (
    <div className={`rounded-xl border p-5 space-y-4 ${accent ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-100 bg-white'}`}>
        {title && (
            <div className={`flex items-center gap-2 pb-3 border-b ${accent ? 'border-emerald-200' : 'border-gray-100'}`}>
                <div className={`w-1 h-4 rounded-full ${accent ? 'bg-[#088A61]' : 'bg-gray-300'}`} />
                <h3 className={`text-[11px] font-black uppercase tracking-widest ${accent ? 'text-[#088A61]' : 'text-gray-500'}`}>{title}</h3>
            </div>
        )}
        {children}
    </div>
);

// ── Confirmation Modal ────────────────────────────────────────────────────────

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    mode: 'draft' | 'submit';
    onConfirmDraft: () => void;
    onConfirmSubmit: () => void;
    isSavingDraft: boolean;
    isSavingSubmit: boolean;
    manuscriptTitle: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen, onClose, mode, onConfirmDraft, onConfirmSubmit, isSavingDraft, isSavingSubmit, manuscriptTitle,
}) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={!isSavingDraft && !isSavingSubmit ? onClose : undefined} />
            <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className={`px-6 py-5 ${mode === 'submit' ? 'bg-gradient-to-r from-[#088A61] to-[#065f44]' : 'bg-gradient-to-r from-gray-700 to-gray-800'}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                                {mode === 'submit' ? <Send className="w-5 h-5 text-white" /> : <Save className="w-5 h-5 text-white" />}
                            </div>
                            <div>
                                <h3 className="text-base font-black text-white">
                                    {mode === 'submit' ? 'Submit Prima Facie Review' : 'Save as Draft'}
                                </h3>
                                <p className="text-xs text-white/70 mt-0.5">
                                    {mode === 'submit' ? 'This action will finalise your review' : 'Your progress will be saved'}
                                </p>
                            </div>
                        </div>
                        {!isSavingDraft && !isSavingSubmit && (
                            <button onClick={onClose} className="text-white/60 hover:text-white transition-colors flex-shrink-0">
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-0.5">Manuscript</p>
                        <p className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">{manuscriptTitle}</p>
                    </div>

                    {mode === 'submit' ? (
                        <>
                            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-amber-700 font-medium leading-relaxed">
                                    Submitting will finalise your Prima Facie review. Make sure all required fields are filled before proceeding.
                                </p>
                            </div>
                            <div className="flex flex-col gap-2 pt-1">
                                <button onClick={onConfirmSubmit} disabled={isSavingSubmit || isSavingDraft}
                                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#088A61] text-white rounded-xl text-sm font-bold hover:bg-[#067451] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed">
                                    {isSavingSubmit
                                        ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />Submitting…</>
                                        : <><Send className="w-4 h-4" />Submit Review</>}
                                </button>
                                <button onClick={onConfirmDraft} disabled={isSavingDraft || isSavingSubmit}
                                    className="w-full flex items-center justify-center gap-2 px-5 py-3 border border-gray-200 text-gray-600 bg-white rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                                    {isSavingDraft
                                        ? <><div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />Saving…</>
                                        : <><Save className="w-4 h-4" />Save as Draft Instead</>}
                                </button>
                                <button onClick={onClose} disabled={isSavingDraft || isSavingSubmit}
                                    className="w-full px-5 py-2.5 text-gray-400 text-sm font-medium hover:text-gray-600 transition-colors disabled:opacity-60">
                                    Cancel
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-blue-700 font-medium leading-relaxed">
                                    Your form data will be saved as a draft. You can return and complete it at any time before submitting.
                                </p>
                            </div>
                            <div className="flex items-center gap-3 pt-1">
                                <button onClick={onClose} disabled={isSavingDraft}
                                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60">
                                    Cancel
                                </button>
                                <button onClick={onConfirmDraft} disabled={isSavingDraft}
                                    className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-800 text-white rounded-xl text-sm font-bold hover:bg-gray-900 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed">
                                    {isSavingDraft
                                        ? <><div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />Saving…</>
                                        : <><Save className="w-4 h-4" />Save Draft</>}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Reviewer sub-form ─────────────────────────────────────────────────────────

interface SuggestedReviewer {
    id: string;
    name: string;
    email: string;
    phoneNo: string;
    affiliateInstitution: string;
}

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

const emptyReviewer = (): SuggestedReviewer => ({ id: ZERO_GUID, name: '', email: '', phoneNo: '', affiliateInstitution: '' });

const ReviewerSubForm = ({
    index, reviewer, onChange, onRemove, canRemove, readOnly,
}: {
    index: number;
    reviewer: SuggestedReviewer;
    onChange: (field: keyof SuggestedReviewer, value: string) => void;
    onRemove: () => void;
    canRemove: boolean;
    readOnly: boolean;
}) => (
    <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-4 space-y-3">
        <div className="flex items-center justify-between">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                Reviewer {index + 1}
            </p>
            {!readOnly && canRemove && (
                <button onClick={onRemove}
                    className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-semibold transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                    Remove
                </button>
            )}
        </div>
        {readOnly ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ROField label="Full Name" value={reviewer.name} />
                <ROField label="Email Address" value={reviewer.email} />
                <ROField label="Phone Number" value={reviewer.phoneNo} />
                <ROField label="Affiliate Institution" value={reviewer.affiliateInstitution} />
            </div>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Full Name" value={reviewer.name} onChange={v => onChange('name', v)} placeholder="e.g. Prof. John Doe" />
                <Field label="Email Address" value={reviewer.email} onChange={v => onChange('email', v)} placeholder="email@example.com" type="email" />
                <Field label="Phone Number" value={reviewer.phoneNo} onChange={v => onChange('phoneNo', v)} placeholder="+234..." />
                <Field label="Affiliate Institution" value={reviewer.affiliateInstitution} onChange={v => onChange('affiliateInstitution', v)} placeholder="e.g. University of Lagos" />
            </div>
        )}
    </div>
);

// ── Form state ────────────────────────────────────────────────────────────────

interface FormData {
    // Objective assessment — active
    conflictOfInterest: string;       // "YES" | "NO" → anyConflictOfInterest boolean
    commentOnTitle: string;
    commentOnContent: string;
    commentOnStructure: string;
    commentOnPedagogy: string;
    contributesToTheory: string;      // "YES" | "NO" → willManuscriptContributeToThisField string
    uniqueAcademicValue: string;
    commentOnReference: string;
    isManuscriptAvailableOnline: string;  // "YES" | "NO" → manuscriptAvailableOnline boolean

    // Newly active fields (were commented out before)
    generalRelevanceToAcademic: string;
    doesNoOfPageMeetGlobalStandard: string;   // "YES" | "NO" → boolean
    stateJudgement: string;
    isAuthorQualifiedToWrite: string;         // "YES" | "NO" → boolean

    // Recommendation — decision dropdown + conditional fields
    decision: string;             // "" | "0" | "1"
    negativeReasons: string;      // → comment; only when decision=1
    recommendedForRetrial: string;// "YES" | "NO" → boolean; only when decision=1

    // Read-only prefilled TAG lead assessor
    tagLeadAssessorName: string;
    tagLeadAssessorInstitution: string;
    tagLeadAssessorSpecialization: string;
    tagLeadAssessorPhone: string;
    tagLeadAssessorEmail: string;

    // Read-only prefilled lead author extras
    leadAuthorInstitution: string;
    leadAuthorSpecialization: string;

    // ── Fields that remain commented out in UI ──
    // manuscriptNoOfPages: string;
    // datePrimaFacieDetermined: string;
    // institutionOfLeadAuthor: string;
    // leadAuthorDepartment: string;
    // areaOfSpecializationLeadAuthor: string;
    // leadAuthorGradeLevel: string;
    // formNumber: string;
    // bookConvenerSig: string;
    // tagChairmanSig: string;
}

const emptyForm = (): FormData => ({
    conflictOfInterest: '',
    commentOnTitle: '',
    commentOnContent: '',
    commentOnStructure: '',
    commentOnPedagogy: '',
    contributesToTheory: '',
    uniqueAcademicValue: '',
    commentOnReference: '',
    isManuscriptAvailableOnline: '',
    generalRelevanceToAcademic: '',
    doesNoOfPageMeetGlobalStandard: '',
    stateJudgement: '',
    isAuthorQualifiedToWrite: '',
    decision: '',
    negativeReasons: '',
    recommendedForRetrial: '',
    tagLeadAssessorName: '',
    tagLeadAssessorInstitution: '',
    tagLeadAssessorSpecialization: '',
    tagLeadAssessorPhone: '',
    tagLeadAssessorEmail: '',
    leadAuthorInstitution: '',
    leadAuthorSpecialization: '',
});

/** Convert a boolean from API → "YES" | "NO" | "" */
const boolToYesNo = (v: boolean | null | undefined): string =>
    v === true ? 'YES' : v === false ? 'NO' : '';

/** Convert a string "YES"/"NO" → boolean for payload */
const yesNoToBool = (v: string): boolean => v === 'YES';

/**
 * Normalise any truthy-yes string from the API → "YES" | "NO" | ""
 * Handles: "Yes", "yes", "YES", "No", "no", "NO", true, false
 */
const normaliseYesNo = (v: string | boolean | null | undefined): string => {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'boolean') return v ? 'YES' : 'NO';
    const upper = String(v).trim().toUpperCase();
    if (upper === 'YES') return 'YES';
    if (upper === 'NO') return 'NO';
    return '';
};

/**
 * Map API decision string → local select value string
 * API returns: "Recommended" | "NotRecommended" (string enum name)
 */
const mapDecision = (d: string | number | null | undefined): string => {
    if (d === 'Recommended' || d === 0) return '0';
    if (d === 'NotRecommended' || d === 1) return '1';
    return '';
};

/** Map API response to local form + reviewer state */
const mapAssignmentToForm = (
    detail: PrimaFacieAssignmentDetail
): { form: Partial<FormData>; reviewers: SuggestedReviewer[] } => {
    const review = detail.primaFacieReview;
    const assignedByUser = detail.assignedByUser;
    const sub = detail.submission;

    const formPatch: Partial<FormData> = {
        tagLeadAssessorName: assignedByUser?.person?.fullName ?? '',
        tagLeadAssessorEmail: assignedByUser?.person?.email ?? '',
        tagLeadAssessorPhone: assignedByUser?.person?.phoneNo ?? '',
        tagLeadAssessorInstitution: assignedByUser?.person?.institution?.name ?? '',
        // Specialization = thematic area of the submission
        tagLeadAssessorSpecialization: sub?.category?.name ?? '',
        // Lead author details prefilled from submission
        leadAuthorInstitution: sub?.author?.person?.institution?.name ?? '',
        leadAuthorSpecialization: sub?.category?.name ?? '',
    };

    if (!review) {
        return { form: formPatch, reviewers: [emptyReviewer()] };
    }

    Object.assign(formPatch, {
        conflictOfInterest: boolToYesNo(review.anyConflictOfInterest),
        commentOnTitle: review.commentOnManuscriptTitleAsAcademicDiscipline ?? '',
        commentOnContent: review.commentOnManuscriptContentAsAcademicDiscipline ?? '',
        commentOnStructure: review.commentOnManuscriptStructure ?? '',
        commentOnPedagogy: review.commentOnManuscriptPedagogy ?? '',
        // API may return "Yes", "YES", "No" — normalise to "YES" | "NO"
        contributesToTheory: normaliseYesNo(review.willManuscriptContributeToThisField),
        uniqueAcademicValue: review.uniqueValueForTetfundFunding ?? '',
        commentOnReference: review.commentOnManuscriptReference ?? '',
        isManuscriptAvailableOnline: boolToYesNo(review.manuscriptAvailableOnline),
        // newly active fields
        generalRelevanceToAcademic: review.generalRelevanceToAcademic ?? '',
        doesNoOfPageMeetGlobalStandard: boolToYesNo(review.doesNoOfPageMeetGlobalStandard),
        stateJudgement: review.stateJudgement ?? '',
        isAuthorQualifiedToWrite: boolToYesNo(review.isAuthorQualifiedToWrite),
        // decision: API returns string enum name "Recommended"/"NotRecommended"
        decision: mapDecision(review.decision),
        negativeReasons: review.comment ?? '',
        recommendedForRetrial: boolToYesNo(review.recommendedForRetrial),
    } as Partial<FormData>);

    // Map existing suggested reviewers — keep their real ids for editing
    const mapped: SuggestedReviewer[] = (review.suggestedReviewers ?? [])
        .slice(0, 3)
        .map(r => ({ id: r.id ?? ZERO_GUID, name: r.name ?? '', email: r.email ?? '', phoneNo: r.phoneNo ?? '', affiliateInstitution: r.affiliateInstitution ?? '' }));

    // Minimum 1 reviewer row
    if (mapped.length === 0) mapped.push(emptyReviewer());

    return { form: formPatch, reviewers: mapped };
};

// ── Required fields for final submit ─────────────────────────────────────────

const validateForSubmit = (form: FormData, reviewers: SuggestedReviewer[]): string[] => {
    const missing: string[] = [];
    if (!form.commentOnTitle.trim()) missing.push('Comment on Manuscript Title');
    if (!form.commentOnContent.trim()) missing.push('Comment on Manuscript Content');
    if (!form.commentOnStructure.trim()) missing.push('Comment on Manuscript Structure');
    if (!form.commentOnPedagogy.trim()) missing.push('Comment on Manuscript Pedagogy');
    if (!form.contributesToTheory) missing.push('Will manuscript contribute to theory?');
    if (!form.uniqueAcademicValue.trim()) missing.push('Unique Academic Value');
    if (!form.commentOnReference.trim()) missing.push('Comment on Reference');
    if (!form.isManuscriptAvailableOnline) missing.push('Manuscript Available Online?');
    if (!form.generalRelevanceToAcademic.trim()) missing.push('General Relevance to Academic');
    if (!form.doesNoOfPageMeetGlobalStandard) missing.push('Does no. of pages meet global standard?');
    if (!form.stateJudgement.trim()) missing.push('State Judgement');
    if (!form.isAuthorQualifiedToWrite) missing.push('Is author qualified to write?');
    if (!form.decision) missing.push('Recommendation / Decision');
    if (form.decision === '1' && !form.negativeReasons.trim()) missing.push('Reason for Not Recommending');
    // Recommended reviewers are only required when the manuscript is Recommended.
    if (form.decision !== '1' && !reviewers[0]?.name.trim()) missing.push('At least one Recommended Reviewer name');
    return missing;
};

// ── Page ──────────────────────────────────────────────────────────────────────

const PrimaFaciePage: React.FC = () => {
    const { submissionId } = useParams<{ submissionId: string }>();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const [assignment, setAssignment] = useState<PrimaFacieAssignmentDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [savingDraft, setSavingDraft] = useState(false);
    const [savingSubmit, setSavingSubmit] = useState(false);
    const [form, setForm] = useState<FormData>(emptyForm());
    const [reviewers, setReviewers] = useState<SuggestedReviewer[]>([emptyReviewer()]);
    const [confirmModal, setConfirmModal] = useState<'draft' | 'submit' | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    // Is the review already submitted? — drives read-only mode
    const isSubmitted = assignment?.primaFacieReview?.submitted === true;

    const set = (field: keyof FormData) => (value: string) =>
        setForm(prev => ({ ...prev, [field]: value }));

    const setReviewer = (index: number, field: keyof SuggestedReviewer, value: string) =>
        setReviewers(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));

    const addReviewer = () => {
        if (reviewers.length < 3) setReviewers(prev => [...prev, emptyReviewer()]);
    };

    const removeReviewer = (index: number) => {
        if (reviewers.length > 1) setReviewers(prev => prev.filter((_, i) => i !== index));
    };

    // ── Load ──────────────────────────────────────────────────────────────────
    const loadAssignment = React.useCallback(async () => {
        if (!submissionId) return;
        setLoading(true);
        try {
            const res = await primaFacieService.getPrimaFacieAssignment(submissionId);
            if (res?.isSuccess === true && res.data) {
                setAssignment(res.data);
                const { form: patch, reviewers: prefillReviewers } = mapAssignmentToForm(res.data);
                setForm(prev => ({ ...prev, ...patch }));
                setReviewers(prefillReviewers);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: res?.message || 'Failed to load Prima Facie assignment',
                });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'Failed to load Prima Facie assignment' });
        } finally {
            setLoading(false);
        }
    }, [submissionId]);

    useEffect(() => {
        loadAssignment();
    }, [loadAssignment]);

    // ── Build payload ─────────────────────────────────────────────────────────
    const buildPayload = (submit: boolean): CreateUpdatePrimaFacieReviewPayload => {
        const notRecommended = form.decision === '1';
        return {
            id: assignment?.primaFacieReview?.id ?? ZERO_GUID,
            primaFacieAssignmentId: submissionId ?? ZERO_GUID,
            decision: form.decision !== '' ? parseInt(form.decision, 10) : 0,
            // comment = negative reasons only when NotRecommended, else empty
            comment: notRecommended ? form.negativeReasons : '',
            // Conflict-of-interest question removed from the UI — always submit "no".
            anyConflictOfInterest: false,
            commentOnManuscriptTitleAsAcademicDiscipline: form.commentOnTitle,
            commentOnManuscriptContentAsAcademicDiscipline: form.commentOnContent,
            commentOnManuscriptStructure: form.commentOnStructure,
            commentOnManuscriptPedagogy: form.commentOnPedagogy,
            willManuscriptContributeToThisField: form.contributesToTheory,  // send "YES"/"NO" string
            uniqueValueForTetfundFunding: form.uniqueAcademicValue,
            commentOnManuscriptReference: form.commentOnReference,
            manuscriptAvailableOnline: yesNoToBool(form.isManuscriptAvailableOnline),
            submit,
            // Don't send recommended reviewers when the manuscript is Not Recommended
            suggestedReviewers: notRecommended
                ? []
                : reviewers
                    .filter(r => r.name || r.email || r.phoneNo)
                    .map(r => ({ id: r.id, name: r.name, email: r.email, phoneNo: r.phoneNo, affiliateInstitution: r.affiliateInstitution })),
            // newly active fields
            generalRelevanceToAcademic: form.generalRelevanceToAcademic,
            doesNoOfPageMeetGlobalStandard: yesNoToBool(form.doesNoOfPageMeetGlobalStandard),
            stateJudgement: form.stateJudgement,
            // recommendedForRetrial only meaningful when NotRecommended
            recommendedForRetrial: notRecommended ? yesNoToBool(form.recommendedForRetrial) : false,
            isAuthorQualifiedToWrite: yesNoToBool(form.isAuthorQualifiedToWrite),
        };
    };

    // ── Execute save draft ────────────────────────────────────────────────────
    const executeSaveDraft = async () => {
        setSavingDraft(true);
        await new Promise(resolve => setTimeout(resolve, 0));
        try {
            const res = await primaFacieService.createUpdatePrimaFacieReview(buildPayload(false));
            if (res?.isSuccess === true) {
                showNotification('success', { title: 'Draft Saved', message: 'Your progress has been saved as a draft.' });
                setConfirmModal(null);
                // Reload so the form reflects the latest saved state
                await loadAssignment();
            } else {
                showNotification('error', { title: 'Error', message: res?.message || 'Failed to save draft. Please try again.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while saving draft.' });
        } finally {
            setSavingDraft(false);
        }
    };

    // ── Execute submit ────────────────────────────────────────────────────────
    const executeSubmit = async () => {
        setSavingSubmit(true);
        // Let React paint the loading state before the network call blocks the thread
        await new Promise(resolve => setTimeout(resolve, 0));
        try {
            const res = await primaFacieService.createUpdatePrimaFacieReview(buildPayload(true));
            if (res?.isSuccess === true) {
                showNotification('success', { title: 'Submitted', message: 'Prima Facie review submitted successfully.' });
                setConfirmModal(null);
                navigate(-1);
            } else {
                showNotification('error', { title: 'Submission Failed', message: res?.message || 'Failed to submit review. Please try again.' });
            }
        } catch (err) {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while submitting.' });
        } finally {
            setSavingSubmit(false);
        }
    };

    const handleDownload = async () => {
        const submissionId = assignment?.submissionId;
        if (!submissionId) return;
        setIsDownloading(true);
        try {
            const res = await submissionVersionService.getUploadedVersionsBySubmissionId(submissionId);
            if (!res.isSuccess || !res.data?.length) {
                showNotification('error', { title: 'Error', message: 'No manuscript versions found.' });
                return;
            }
            const latest = [...res.data].sort((a, b) => b.versionNumber - a.versionNumber)[0];
            const url = latest.manuscriptUrl.startsWith('http')
                ? latest.manuscriptUrl
                : `${FILE_BASE_URL}/${latest.manuscriptUrl}`;
            await downloadFileFromUrl(url, `Manuscript_${assignment?.submission?.submissionNumber}_v${latest.versionNumber}.docx`);
        } catch {
            showNotification('error', { title: 'Error', message: 'Failed to download manuscript.' });
        } finally {
            setIsDownloading(false);
        }
    };

    // ── Open submit modal with validation ─────────────────────────────────────
    const handleOpenSubmitModal = () => {
        const errors = validateForSubmit(form, reviewers);
        if (errors.length > 0) {
            showNotification('error', {
                title: 'Incomplete Form',
                message: `Please fill in the following before submitting: ${errors.join(', ')}.`,
            });
            return;
        }
        setConfirmModal('submit');
    };

    // ── Derived display values ────────────────────────────────────────────────
    const sub = assignment?.submission;
    const adminNo    = sub?.submissionNumber ?? '—';
    const title      = sub?.title ?? '—';
    const leadAuthor = sub?.author?.person?.fullName || sub?.authors?.split(',')[0]?.trim() || '—';
    const plagiarism = sub?.plagiarismOutcome !== undefined ? `${sub.plagiarismOutcome}%` : '—';
    const category   = sub?.category?.name ?? '—';
    const appType    = sub?.applicationType === 1 ? 'Book' : sub?.applicationType === 2 ? 'Journal Article' : '—';

    const notRecommended = form.decision === '1';

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) return (
        <div className="flex h-96 items-center justify-center">
            <div className="w-10 h-10 border-4 border-gray-100 border-t-[#088A61] rounded-full animate-spin" />
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="w-full max-w-6xl mx-auto px-4 pb-24">
            <style>{PRIMA_FACIE_PRINT_STYLES}</style>

            <ConfirmModal
                isOpen={confirmModal !== null}
                onClose={() => !savingDraft && !savingSubmit && setConfirmModal(null)}
                mode={confirmModal ?? 'draft'}
                onConfirmDraft={executeSaveDraft}
                onConfirmSubmit={executeSubmit}
                isSavingDraft={savingDraft}
                isSavingSubmit={savingSubmit}
                manuscriptTitle={title}
            />

            <div className="print:hidden">
            {/* ── Hero Header ───────────────────────────────────────────────── */}
            <div className="mb-8">
                <button onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 font-medium transition-colors mb-6 group">
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                    Back to Submissions
                </button>

                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#088A61] to-[#065f44] px-8 py-7 shadow-lg">
                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
                    <div className="absolute -bottom-10 -right-2 w-56 h-56 rounded-full bg-white/5" />
                    <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                                <FileSearch className="w-6 h-6 text-white" />
                            </div>
                            <div id="printContent" className="space-y-5">
                                <p className="text-[10px] font-bold text-emerald-200 uppercase tracking-widest mb-0.5">
                                    Tertiary Education Trust Fund — TAG
                                </p>
                                <h1 className="text-xl sm:text-2xl font-black text-white leading-tight">Prima Facie Evaluation</h1>
                                <p className="text-lg font-bold text-white mt-0.5">
                                    {title} (<span>{adminNo}</span>)
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => window.print()}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors border border-white/20 self-start sm:self-center">
                                <Printer className="w-4 h-4" />Print
                            </button>
                            <button onClick={handleDownload} disabled={isDownloading}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white text-[#088A61] text-sm font-bold hover:bg-emerald-50 transition-colors self-start sm:self-center disabled:opacity-50 shadow-sm">
                                {isDownloading
                                    ? <><div className="w-4 h-4 border-2 border-[#088A61]/20 border-t-[#088A61] rounded-full animate-spin" />Downloading...</>
                                    : <><Download className="w-4 h-4" />Download Manuscript</>}
                            </button>
                        </div>
                    </div>
                    <div id="printContent" className="space-y-5">
                        <div className="relative mt-5 flex flex-wrap gap-3">
                        {[
                            { label: 'Type', value: appType },
                            { label: 'Thematic Area', value: category },
                            { label: 'Similarity', value: plagiarism },
                            ...(assignment?.isLeader ? [{ label: 'Role', value: 'Lead Assessor' }] : []),
                            ...(isSubmitted ? [{ label: 'Status', value: 'Submitted' }] : []),
                        ].map(item => (
                            <div key={item.label} className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5 border border-white/15">
                                <span className="text-[10px] font-bold text-emerald-300 uppercase">{item.label}</span>
                                <span className="text-xs font-bold text-white">{item.value}</span>
                            </div>
                        ))}
                    </div>
                    </div>
                    
                </div>
            </div>

            {/* ── Notices ───────────────────────────────────────────────────── */}
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-[11px] font-bold text-amber-700 uppercase tracking-wide">
                    This form should not move out of TETFund Administrative Office / TAG Meetings
                </p>
            </div>

            {isSubmitted && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <p className="text-[11px] font-bold text-green-700 uppercase tracking-wide">
                        This Prima Facie review has been submitted and is now read-only.
                    </p>
                </div>
            )}

            {/* ── Form Body ─────────────────────────────────────────────────── */}
            <div id="printContent" className="space-y-5">

                {/* 1 — Manuscript Identification (read-only) */}
                <SectionCard title="Manuscript Identification">
                    {/*
                    ── COMMENTED OUT ──
                    <Field label="Manuscript No. of Pages" ... />
                    <input type="date" ... /> (Date Prima Facie Determined)
                    */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ROField label="Manuscript Admin. No." value={adminNo} />
                        <ROField label="Plagiarism Test Result (%)" value={plagiarism} />
                    </div>
                    <div className="grid grid-cols-1 gap-3 mt-2">
                        <ROField label="Manuscript Title" value={title} fullWidth />
                        <ROField label="Name of Lead Author" value={leadAuthor} fullWidth />
                        {/* Institution and area of specialization of lead author — from submission data */}
                        <ROField label="Institution of Lead Author" value={form.leadAuthorInstitution} fullWidth />
                        <ROField label="Area of Specialization of Lead Author" value={form.leadAuthorSpecialization} fullWidth />
                        {/*
                        ── COMMENTED OUT — manual lead author detail fields ──
                        <Field label="Lead Author's Department" value={form.leadAuthorDepartment} onChange={set('leadAuthorDepartment')} />
                        <Field label="Lead Author's Grade Level" value={form.leadAuthorGradeLevel} onChange={set('leadAuthorGradeLevel')} />
                        */}
                    </div>
                </SectionCard>

                {/*
                ── COMMENTED OUT — Lead Author Details ──
                <SectionCard title="Lead Author Details">
                    Institution, Department, Area of Specialization, Grade Level
                </SectionCard>
                */}

                {/* 2 — TAG Lead Assessor Details (read-only) */}
                <SectionCard title="TAG Lead Assessor Details">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ROField label="Name of TAG Lead Assessor" value={form.tagLeadAssessorName} fullWidth />
                        <ROField label="Institution of TAG Lead Assessor" value={form.tagLeadAssessorInstitution} />
                        <ROField label="Area of Specialization" value={form.tagLeadAssessorSpecialization} />
                        <ROField label="Phone Number" value={form.tagLeadAssessorPhone} />
                        <ROField label="Email Address" value={form.tagLeadAssessorEmail} />
                    </div>
                </SectionCard>

                {/* 3 — Objective Assessment */}
                <SectionCard title="Objective Assessment — Fill this section objectively as an academic document for Tertiary Institution use" accent>
                    <div className="grid grid-cols-1 gap-5">

                        {/* Conflict of Interest question removed from the UI — always submitted as "no". */}

                        {/*
                        ── COMMENTED OUT — isAuthorsQualified moved here then commented ──
                        <YesNo name="isAuthorsQualified" label="Is/Are the Author(s) Scholars who are qualified to write Book(s) for Nigerian Tertiary Institution?" ... />
                        */}

                        {isSubmitted ? (
                            <>
                                <ROTextArea label="Comment on Manuscript's Title as an Academic Discipline Topic for Tertiary Institution" value={form.commentOnTitle} rows={3} fullWidth />
                                <ROTextArea label="Comment on Manuscript's Content as a Tertiary Institution Document" value={form.commentOnContent} rows={3} fullWidth />
                                <ROTextArea label="Comment on Manuscript's Structure" value={form.commentOnStructure} rows={2} fullWidth />
                                <ROTextArea label="Comment on Manuscript's Pedagogy" value={form.commentOnPedagogy} rows={2} fullWidth />
                                <ROYesNo label="Will this manuscript Contribute to the Theory and Concept of Academic Knowledge in its Field?" value={form.contributesToTheory} />
                                <ROTextArea label="State its Unique Academic Value in its Field for TETFund to Fund it" value={form.uniqueAcademicValue} rows={3} fullWidth />
                                <ROTextArea label="Comment on Manuscript's Reference to the Above Issues" value={form.commentOnReference} rows={2} fullWidth />
                                <ROYesNo label="Is the full Manuscript or Significant Part of it Available Online?" value={form.isManuscriptAvailableOnline} />
                                <ROTextArea label="General Relevance to Academic and Tertiary Institutional Use" value={form.generalRelevanceToAcademic} rows={2} fullWidth />
                                <ROYesNo label="Does the No. of Pages meet the Global Practices for such a Text?" value={form.doesNoOfPageMeetGlobalStandard} />
                                <ROField label="State Judgement" value={form.stateJudgement} fullWidth />
                                <ROYesNo label="Is/Are the Author(s) Scholars who are qualified to write Book(s) for Nigerian Tertiary Institution?" value={form.isAuthorQualifiedToWrite} />
                            </>
                        ) : (
                            <>
                                <TextArea label="Comment on Manuscript's Title as an Academic Discipline Topic for Tertiary Institution" value={form.commentOnTitle} onChange={set('commentOnTitle')} rows={3} fullWidth />
                                <TextArea label="Comment on Manuscript's Content as a Tertiary Institution Document" value={form.commentOnContent} onChange={set('commentOnContent')} rows={3} fullWidth />
                                <TextArea label="Comment on Manuscript's Structure" value={form.commentOnStructure} onChange={set('commentOnStructure')} rows={2} fullWidth />
                                <TextArea label="Comment on Manuscript's Pedagogy" value={form.commentOnPedagogy} onChange={set('commentOnPedagogy')} rows={2} fullWidth />
                                <YesNo name="contributesToTheory" label="Will this manuscript Contribute to the Theory and Concept of Academic Knowledge in its Field?" value={form.contributesToTheory} onChange={set('contributesToTheory')} />
                                <TextArea label="State its Unique Academic Value in its Field for TETFund to Fund it" value={form.uniqueAcademicValue} onChange={set('uniqueAcademicValue')} rows={3} fullWidth />
                                <TextArea label="Comment on Manuscript's Reference to the Above Issues" value={form.commentOnReference} onChange={set('commentOnReference')} rows={2} fullWidth />
                                <YesNo name="isManuscriptAvailableOnline" label="Is the full Manuscript or Significant Part of it Available Online?" value={form.isManuscriptAvailableOnline} onChange={set('isManuscriptAvailableOnline')} />
                                <TextArea label="General Relevance to Academic and Tertiary Institutional Use" value={form.generalRelevanceToAcademic} onChange={set('generalRelevanceToAcademic')} rows={2} fullWidth />
                                <YesNo name="doesNoOfPageMeetGlobalStandard" label="Does the No. of Pages meet the Global Practices for such a Text?" value={form.doesNoOfPageMeetGlobalStandard} onChange={set('doesNoOfPageMeetGlobalStandard')} />
                                <Field label="State Judgement" value={form.stateJudgement} onChange={set('stateJudgement')} placeholder="State your judgement on the page count…" fullWidth />
                                <YesNo name="isAuthorQualifiedToWrite" label="Is/Are the Author(s) Scholars who are qualified to write Book(s) for Nigerian Tertiary Institution?" value={form.isAuthorQualifiedToWrite} onChange={set('isAuthorQualifiedToWrite')} />
                            </>
                        )}
                    </div>
                </SectionCard>

                {/* 4 — Recommendation */}
                <SectionCard title="Recommendation">
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">
                                Clearly State your Recommendation <span className="text-red-500">*</span>
                            </p>
                            {isSubmitted ? (
                                <div className="w-full px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 min-h-[38px]">
                                    {form.decision === '0' ? 'Recommended' : form.decision === '1' ? 'Not Recommended' : <span className="italic text-gray-400">—</span>}
                                </div>
                            ) : (
                                <select
                                    value={form.decision}
                                    onChange={e => {
                                        set('decision')(e.target.value);
                                        // Clear conditional fields when switching to Recommended
                                        if (e.target.value !== '2') {
                                            set('negativeReasons')('');
                                            set('recommendedForRetrial')('');
                                        }
                                    }}
                                    className="block w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#088A61] focus:border-transparent"
                                >
                                    <option value="">Select recommendation…</option>
                                    <option value="0">Recommended</option>
                                    <option value="1">Not Recommended</option>
                                </select>
                            )}
                        </div>

                        {/* Conditional — only shown when Not Recommended */}
                        {(notRecommended || (isSubmitted && form.decision === '1')) && (
                            <>
                                {isSubmitted ? (
                                    <ROTextArea label="If negative, give some reasons for the author to improve its quality in future" value={form.negativeReasons} rows={3} fullWidth />
                                ) : (
                                    <TextArea
                                        label="If negative, give some reasons for the author to improve its quality in future"
                                        value={form.negativeReasons}
                                        onChange={set('negativeReasons')}
                                        rows={3} fullWidth
                                        placeholder="Provide reasons for not recommending…"
                                    />
                                )}
                                {isSubmitted ? (
                                    <ROYesNo label="Can the Manuscript be Re-submitted for another Trial if your Suggestions are Considered?" value={form.recommendedForRetrial} />
                                ) : (
                                    <YesNo
                                        name="recommendedForRetrial"
                                        label="Can the Manuscript be Re-submitted for another Trial if your Suggestions are Considered?"
                                        value={form.recommendedForRetrial}
                                        onChange={set('recommendedForRetrial')}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </SectionCard>

                {/* 5 — Recommended Reviewers (min 1, max 3) — only when Recommended */}
                {!notRecommended && (
                <SectionCard title="Recommended Reviewers">
                    <div className="space-y-4">
                        {reviewers.map((reviewer, index) => (
                            <ReviewerSubForm
                                key={index}
                                index={index}
                                reviewer={reviewer}
                                onChange={(field, value) => setReviewer(index, field, value)}
                                onRemove={() => removeReviewer(index)}
                                canRemove={reviewers.length > 1}
                                readOnly={isSubmitted}
                            />
                        ))}
                    </div>
                    {!isSubmitted && reviewers.length < 3 && (
                        <button
                            onClick={addReviewer}
                            className="mt-3 flex items-center gap-2 px-4 py-2 border border-dashed border-[#088A61] text-[#088A61] rounded-lg text-sm font-semibold hover:bg-emerald-50 transition-colors w-full justify-center"
                        >
                            <Plus className="w-4 h-4" />
                            Add Another Reviewer ({reviewers.length}/3)
                        </button>
                    )}
                    {/*
                    ── COMMENTED OUT — old single-reviewer extra fields ──
                    Address, TAG member who suggested, sub-group assessor phones/names/signatures
                    */}
                </SectionCard>
                )}

                {/*
                ── COMMENTED OUT — Official Use section ──
                Form Number, Book Convener's Signature, TAG Chairman's Signature
                */}

            </div>

            {/* ── Sticky footer — hidden when submitted ─────────────────────── */}
            {!isSubmitted && (
                <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-100 px-6 py-3 flex justify-end gap-3 shadow-lg">
                    <button onClick={() => navigate(-1)} disabled={savingDraft || savingSubmit}
                        className="px-5 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50">
                        Cancel
                    </button>
                    <button onClick={() => setConfirmModal('draft')} disabled={savingDraft || savingSubmit}
                        className="flex items-center gap-2 px-5 py-2 border border-[#088A61] text-[#088A61] bg-white rounded-lg text-sm font-bold hover:bg-emerald-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        <Save className="w-4 h-4" />Save Draft
                    </button>
                    <button onClick={handleOpenSubmitModal} disabled={savingDraft || savingSubmit}
                        className="flex items-center gap-2 px-6 py-2 bg-[#088A61] text-white rounded-lg text-sm font-bold hover:bg-[#067451] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                        <Send className="w-4 h-4" />Submit Review
                    </button>
                </div>
            )}
            </div>

            <PrimaFaciePrintView
                adminNo={adminNo}
                title={title}
                leadAuthor={leadAuthor}
                plagiarism={plagiarism}
                category={category}
                appType={appType}
                form={form}
                reviewers={reviewers}
                notRecommended={notRecommended}
            />
        </div>
    );
};

// ── Print-only view ──────────────────────────────────────────────────────────

interface PrimaFaciePrintViewProps {
    adminNo: string;
    title: string;
    leadAuthor: string;
    plagiarism: string;
    category: string;
    appType: string;
    form: FormData;
    reviewers: SuggestedReviewer[];
    notRecommended: boolean;
}

const PrintFieldPF = ({ label, value }: { label: string; value?: string }) => (
    <div style={{ marginBottom: '6pt' }}>
        <div style={{ fontSize: '8pt', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: '9.5pt', whiteSpace: 'pre-wrap', borderBottom: '0.5pt solid #555', paddingBottom: '2pt', minHeight: '14pt' }}>
            {value && value.trim() ? value : '—'}
        </div>
    </div>
);

const PrintYesNoPF = ({ label, value }: { label: string; value: string }) => (
    <div style={{ marginBottom: '6pt', fontSize: '9.5pt' }}>
        <span style={{ fontWeight: 700 }}>{label}</span>{' '}
        <span style={{ marginLeft: '4pt' }}>{value || '—'}</span>
    </div>
);

const sectionHeaderPF = (label: string) => (
    <h3 style={{ fontSize: '10.5pt', fontWeight: 800, textTransform: 'uppercase', borderBottom: '1pt solid #000', paddingBottom: '3pt', marginBottom: '8pt' }}>
        {label}
    </h3>
);

const PrimaFaciePrintView: React.FC<PrimaFaciePrintViewProps> = (p) => {
    const recommendationLabel =
        p.form.decision === '0' ? 'Recommended'
            : p.form.decision === '1' ? 'Not Recommended'
                : '—';

    return (
        <div className="hidden print:block pf-print-doc" style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: '#000', background: 'white', fontSize: '10pt', lineHeight: 1.4 }}>

            {/* ── HEADER ── */}
            <div className="pf-print-section" style={{ borderBottom: '2pt solid #000', paddingBottom: '8pt', marginBottom: '12pt', textAlign: 'center' }}>
                <p style={{ fontSize: '9pt', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, margin: 0 }}>
                    Tertiary Education Trust Fund — TAG
                </p>
                <h1 style={{ fontSize: '18pt', fontWeight: 800, margin: '4pt 0 2pt 0', lineHeight: 1.1 }}>
                    Prima Facie Evaluation
                </h1>
                <p style={{ fontSize: '10pt', margin: '0 0 4pt 0', fontStyle: 'italic' }}>
                    {p.title} ({p.adminNo})
                </p>
                <p style={{ fontSize: '9pt', marginTop: '4pt', marginBottom: 0 }}>
                    <strong>Type:</strong> {p.appType} &nbsp;·&nbsp;
                    <strong>Thematic Area:</strong> {p.category} &nbsp;·&nbsp;
                    <strong>Similarity:</strong> {p.plagiarism}
                </p>
            </div>

            {/* ── NOTICE ── */}
            <div className="pf-print-section" style={{ border: '0.75pt solid #000', padding: '4pt 8pt', marginBottom: '10pt', textAlign: 'center', fontSize: '9pt', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                This form should not move out of TETFund Administrative Office / TAG Meetings
            </div>

            {/* ── 1. MANUSCRIPT IDENTIFICATION ── */}
            <div className="pf-print-section" style={{ marginBottom: '14pt' }}>
                {sectionHeaderPF('1. Manuscript Identification')}
                <table style={{ width: '100%', fontSize: '9.5pt', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top', width: '36%' }}>Manuscript Admin No.</td><td style={{ padding: '2pt 0' }}>{p.adminNo}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Plagiarism Test Result</td><td style={{ padding: '2pt 0' }}>{p.plagiarism}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Manuscript Title</td><td style={{ padding: '2pt 0' }}>{p.title}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Name of Lead Author</td><td style={{ padding: '2pt 0' }}>{p.leadAuthor}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Institution of Lead Author</td><td style={{ padding: '2pt 0' }}>{p.form.leadAuthorInstitution || '—'}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Area of Specialization of Lead Author</td><td style={{ padding: '2pt 0' }}>{p.form.leadAuthorSpecialization || '—'}</td></tr>
                    </tbody>
                </table>
            </div>

            {/* ── 2. TAG LEAD ASSESSOR DETAILS ── */}
            <div className="pf-print-section" style={{ marginBottom: '14pt' }}>
                {sectionHeaderPF('2. TAG Lead Assessor Details')}
                <table style={{ width: '100%', fontSize: '9.5pt', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top', width: '36%' }}>Name of TAG Lead Assessor</td><td style={{ padding: '2pt 0' }}>{p.form.tagLeadAssessorName || '—'}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Institution</td><td style={{ padding: '2pt 0' }}>{p.form.tagLeadAssessorInstitution || '—'}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Area of Specialization</td><td style={{ padding: '2pt 0' }}>{p.form.tagLeadAssessorSpecialization || '—'}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Phone Number</td><td style={{ padding: '2pt 0' }}>{p.form.tagLeadAssessorPhone || '—'}</td></tr>
                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', verticalAlign: 'top' }}>Email Address</td><td style={{ padding: '2pt 0' }}>{p.form.tagLeadAssessorEmail || '—'}</td></tr>
                    </tbody>
                </table>
            </div>

            {/* ── 3. OBJECTIVE ASSESSMENT ── */}
            <div style={{ marginBottom: '12pt' }}>
                {sectionHeaderPF('3. Objective Assessment')}
                <p style={{ fontSize: '8pt', fontStyle: 'italic', margin: '0 0 6pt 0' }}>
                    Filled objectively as an academic document for tertiary institution use.
                </p>
                <PrintFieldPF label="Comment on Manuscript's Title as an Academic Discipline Topic for Tertiary Institution" value={p.form.commentOnTitle} />
                <PrintFieldPF label="Comment on Manuscript's Content as a Tertiary Institution Document" value={p.form.commentOnContent} />
                <PrintFieldPF label="Comment on Manuscript's Structure" value={p.form.commentOnStructure} />
                <PrintFieldPF label="Comment on Manuscript's Pedagogy" value={p.form.commentOnPedagogy} />
                <PrintYesNoPF label="Will this manuscript contribute to the theory and concept of academic knowledge in its field?" value={p.form.contributesToTheory} />
                <PrintFieldPF label="State its Unique Academic Value in its Field for TETFund to Fund it" value={p.form.uniqueAcademicValue} />
                <PrintFieldPF label="Comment on Manuscript's Reference to the Above Issues" value={p.form.commentOnReference} />
                <PrintYesNoPF label="Is the full Manuscript or significant part of it available online?" value={p.form.isManuscriptAvailableOnline} />
                <PrintFieldPF label="General Relevance to Academic and Tertiary Institutional Use" value={p.form.generalRelevanceToAcademic} />
                <PrintYesNoPF label="Does the No. of Pages meet the Global Practices for such a Text?" value={p.form.doesNoOfPageMeetGlobalStandard} />
                <PrintFieldPF label="State Judgement" value={p.form.stateJudgement} />
                <PrintYesNoPF label="Is/Are the Author(s) Scholars qualified to write Book(s) for Nigerian Tertiary Institution?" value={p.form.isAuthorQualifiedToWrite} />
            </div>

            {/* ── 4. RECOMMENDATION ── */}
            <div className="pf-print-section" style={{ marginBottom: '12pt', border: '1pt solid #000', padding: '8pt 10pt' }}>
                {sectionHeaderPF('4. Recommendation')}
                <p style={{ fontSize: '11pt', fontWeight: 800, margin: '0 0 4pt 0' }}>
                    Decision: {recommendationLabel}
                </p>
                {p.notRecommended && (
                    <>
                        <PrintFieldPF label="If negative, reasons for the author to improve quality in future" value={p.form.negativeReasons} />
                        <PrintYesNoPF label="Can the manuscript be re-submitted for another trial if your suggestions are considered?" value={p.form.recommendedForRetrial} />
                    </>
                )}
            </div>

            {/* ── 5. RECOMMENDED REVIEWERS (only when Recommended) ── */}
            {!p.notRecommended && p.reviewers.length > 0 && (
                <div style={{ marginBottom: '12pt' }}>
                    {sectionHeaderPF('5. Recommended Reviewers')}
                    {p.reviewers.map((r, idx) => {
                        const hasAny = r.name || r.email || r.phoneNo || r.affiliateInstitution;
                        if (!hasAny) return null;
                        return (
                            <div key={idx} className="pf-print-section" style={{ border: '0.75pt solid #000', padding: '6pt 8pt', marginBottom: '8pt' }}>
                                <h4 style={{ fontSize: '10pt', fontWeight: 800, textTransform: 'uppercase', margin: '0 0 4pt 0' }}>
                                    Reviewer {idx + 1}
                                </h4>
                                <table style={{ width: '100%', fontSize: '9.5pt', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0', width: '32%' }}>Full Name</td><td style={{ padding: '2pt 0' }}>{r.name || '—'}</td></tr>
                                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0' }}>Email Address</td><td style={{ padding: '2pt 0' }}>{r.email || '—'}</td></tr>
                                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0' }}>Phone Number</td><td style={{ padding: '2pt 0' }}>{r.phoneNo || '—'}</td></tr>
                                        <tr><td style={{ fontWeight: 700, padding: '2pt 6pt 2pt 0' }}>Affiliate Institution</td><td style={{ padding: '2pt 0' }}>{r.affiliateInstitution || '—'}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── SIGNATURE ── */}
            <div className="pf-print-section" style={{ marginTop: '18pt', paddingTop: '8pt', borderTop: '1pt solid #000' }}>
                <table style={{ width: '100%', fontSize: '9.5pt' }}>
                    <tbody>
                        <tr>
                            <td style={{ verticalAlign: 'top', paddingRight: '12pt', width: '60%' }}>
                                <p style={{ margin: 0 }}>TAG Lead Assessor's Signature: ____________________________</p>
                                <p style={{ margin: '6pt 0 0 0' }}>Name: {p.form.tagLeadAssessorName || '____________________________'}</p>
                                <p style={{ margin: '6pt 0 0 0' }}>Date: ____________________________</p>
                            </td>
                            <td style={{ verticalAlign: 'top', borderLeft: '0.5pt solid #999', paddingLeft: '12pt' }}>
                                <p style={{ margin: 0 }}><strong>Decision:</strong> {recommendationLabel}</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default PrimaFaciePage;